import * as Yup from 'yup';

import { RECURRENCE_TYPE } from '@/constants';

export type Recipient = {
  name: string;
  phoneNumber: string;
  metadata: Record<string, any>; //eslint-disable-line @typescript-eslint/no-explicit-any
  status: 'valid' | 'invalid';
};

const recipientSchema = Yup.object().shape({
  name: Yup.string().required(),
  phoneNumber: Yup.string().required(),
  metadata: Yup.object().optional(),
  status: Yup.string().oneOf(['valid', 'invalid']).required(),
});

const recurringFields = {
  isRecurring: Yup.boolean().default(false),
  recurringFrequency: Yup.string().when('isRecurring', {
    is: true,
    then: schema =>
      schema.oneOf(RECURRENCE_TYPE.map(r => r.value)).required('Please select a frequency'),
    otherwise: schema => schema.optional(),
  }),
  recurringDaysOfWeek: Yup.array().when(['isRecurring', 'recurringFrequency'], {
    is: (isRecurring: boolean, freq: string) => isRecurring && freq === 'week',
    then: schema =>
      schema.of(Yup.string().required()).min(1, 'Please select at least one day').required(),
    otherwise: schema => schema,
  }),
};

export const batchCallSchema = Yup.object().shape({
  batchName: Yup.string().trim().required('Batch call name is required'),
  agentId: Yup.string().required('Please select a from number'),
  recipients: Yup.array()
    .of(recipientSchema)
    .min(1, 'Please upload recipients')
    .test('all-valid', (values, ctx) => {
      const invalidCount = values?.filter(r => r.status === 'invalid').length ?? 0;
      if (invalidCount > 0) {
        return ctx.createError({
          message: `${invalidCount} recipient(s) have invalid phone numbers`,
        });
      }
      return true;
    }),
  uploadedFileName: Yup.string(),
  scheduledDate: Yup.string().required('Please select a date'),
  scheduledTime: Yup.string().required('Please select a time'),
  scheduledTimezone: Yup.string().required('Please select a timezone'),
  ...recurringFields,
});

export type BatchCallFormValues = Yup.InferType<typeof batchCallSchema>;

export const batchCallUpdateSchema = Yup.object().shape({
  batchName: Yup.string().trim().required('Batch call name is required'),
  agentId: Yup.string().required('Please select a from number'),
  scheduledDate: Yup.string().required('Please select a date'),
  scheduledTime: Yup.string().required('Please select a time'),
  scheduledTimezone: Yup.string().required('Please select a timezone'),
  ...recurringFields,
});

export type BatchCallUpdateFormValues = Yup.InferType<typeof batchCallUpdateSchema>;

export const recipientUpdateSchema = Yup.object().shape({
  name: Yup.string().trim().required('Name is required'),
  phoneNumber: Yup.string()
    .trim()
    .required('Phone number is required')
    .matches(/^\+?[\d\s\-().]{7,}$/, 'Enter a valid phone number'),
});

export type RecipientUpdateFormValues = Yup.InferType<typeof recipientUpdateSchema>;

export type RecurringValues = Pick<
  BatchCallFormValues | BatchCallUpdateFormValues,
  'isRecurring' | 'recurringFrequency' | 'recurringDaysOfWeek'
>;
